import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Callout, Chip } from '@/nova/components';

import { DevicePreviewPill } from '../DevicePreviewPill';
import { PreviewFrame } from '../PlayableComposer/PreviewFrame';
import { BROWSER_BAR_H, FRAME_EDGE, landingFrameLayout, type PreviewDevice } from './preview-layout';
import * as S from './LandingPageComposer.styles';

export { DEVICE_VIEWPORT, type PreviewDevice } from './preview-layout';

/** Why the latest preview could not be built: the template is unknown, previews are not served here, or it failed. */
export type PreviewFailure = 'unknown-template' | 'unavailable' | 'failed';

export interface LandingPreviewProps {
  /** The latest preview document (kept on screen while the next one builds); null before the first. */
  html: string | null;
  /** A preview is being built. */
  busy: boolean;
  failure: PreviewFailure | null;
  /** Why there is nothing to preview yet (no template chosen or loadable), or null. Translated. */
  waitingText: string | null;
  /** The page is a full-screen game: shown as a whole device screen (fitted to the pane) with Replay. */
  immersive?: boolean;
  /** The frame's top edge (a mock browser bar), drawn over the page at its own size. */
  addressBar?: React.ReactNode;
  /** The head's right side (the composer's publish controls). */
  actions?: React.ReactNode;
  /** Shown between the head and the frame (what publishing has to say). */
  notice?: React.ReactNode;
}

/** The widths a landing page previews at (the phone's icon pill offers these two). */
const PREVIEW_MODES = ['desktop', 'mobile'] as const;

interface Box {
  w: number;
  h: number;
}

/**
 * The composer's preview: the page as the server renders it, in a frame sandboxed to scripts only with its links
 * inert, under an optional mock browser bar. The head holds the Desktop / Mobile toggle and, for a full-screen game,
 * Replay (it loads the game afresh) on the left, and the caller's actions on the right. A page template is laid out
 * at a desktop or phone width, scaled to the pane's width (never enlarged) and as tall as the pane; the page scrolls
 * inside its frame. A full-screen game on a phone is a real 390x844 phone fitted into the pane; on a desktop it takes
 * the pane's own shape (kept between 16:9 and 4:3, as a visitor with that window sees it). The frame spans the pane's
 * width and hugs the page's height; a new document fades in over the one on screen, so an edit never blanks it.
 */
export const LandingPreview = ({
  html,
  busy,
  failure,
  waitingText,
  immersive = false,
  addressBar,
  actions,
  notice,
}: LandingPreviewProps): React.ReactElement => {
  const { t } = useTranslation();
  const [device, setDevice] = React.useState<PreviewDevice>('desktop');
  const [replays, setReplays] = React.useState(0);
  const stageRef = React.useRef<HTMLDivElement>(null);
  const [box, setBox] = React.useState<Box | null>(null);

  // Syncs with the layout: the stage's inner size (the frame's budget; nothing in it ever grows it).
  React.useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const read = (): void =>
      setBox((cur) =>
        cur && cur.w === el.clientWidth && cur.h === el.clientHeight ? cur : { w: el.clientWidth, h: el.clientHeight },
      );
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const barH = addressBar ? BROWSER_BAR_H : 0;
  const layout = box
    ? landingFrameLayout({
        device,
        immersive,
        w: box.w - 2 * FRAME_EDGE,
        h: box.h - barH - 2 * FRAME_EDGE,
      })
    : null;
  // The page's box on screen: the frame hugs its height.
  const pageH = layout ? Math.round(layout.docH * layout.scale) : 0;

  return (
    <>
      <S.PreviewHead>
        <S.PreviewHeadSide>
          <S.WidthChips role="group" aria-label={t('Preview width')}>
            <Chip
              label={t('Desktop')}
              value="desktop"
              selected={device === 'desktop'}
              onClick={() => setDevice('desktop')}
            />
            <Chip
              label={t('Mobile')}
              value="mobile"
              selected={device === 'mobile'}
              onClick={() => setDevice('mobile')}
            />
          </S.WidthChips>
          <S.PhoneDevicePill>
            <DevicePreviewPill
              modes={PREVIEW_MODES}
              size="touch"
              value={device}
              onChange={(mode) => setDevice(mode === 'desktop' ? 'desktop' : 'mobile')}
            />
          </S.PhoneDevicePill>
          {immersive && html ? (
            <Button variant="text" icon="refresh" label={t('Replay')} onClick={() => setReplays((n) => n + 1)} />
          ) : null}
          {busy && html ? (
            <S.PreviewStatus aria-live="polite">
              <S.Spinner aria-hidden="true" />
              <S.NoteText text="Updating the preview…" />
            </S.PreviewStatus>
          ) : null}
        </S.PreviewHeadSide>
        {actions}
      </S.PreviewHead>

      {notice}

      {failure === 'unknown-template' ? (
        <Callout tone="warning" text={t("This template isn't available any more. Pick another one.")} />
      ) : failure === 'unavailable' ? (
        <Callout tone="info" text={t("Landing page previews aren't available in this environment yet.")} />
      ) : failure === 'failed' ? (
        <Callout tone="warning" text={t("The preview couldn't be built. It retries on your next edit.")} />
      ) : null}

      <S.Stage ref={stageRef}>
        {layout ? (
          <S.FrameCard>
            {addressBar}
            <S.FrameViewport $h={pageH}>
              {html ? (
                <PreviewFrame
                  html={html}
                  w={layout.docW}
                  h={layout.docH}
                  scale={layout.scale}
                  run={replays}
                  title={t('Landing page preview')}
                />
              ) : waitingText ? (
                <S.FrameNote>
                  <S.NoteText raw text={waitingText} />
                </S.FrameNote>
              ) : failure ? null : (
                <S.FrameNote>
                  <S.Spinner aria-hidden="true" />
                  <S.NoteText text="Building the preview…" />
                </S.FrameNote>
              )}
            </S.FrameViewport>
          </S.FrameCard>
        ) : null}
      </S.Stage>
    </>
  );
};

export default LandingPreview;
